import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { User, login as apiLogin, getCurrentUser, ensureMediaToken } from '../api/client'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  isTrainer: boolean
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshUser = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        setUser(null)
        return
      }
      const userData = await getCurrentUser()
      setUser(userData)
      localStorage.setItem('user', JSON.stringify(userData))
      // Warm the media token used by <img src> image/thumbnail URLs
      ensureMediaToken()
    } catch {
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      setUser(null)
    }
  }

  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true)
      try {
        const token = localStorage.getItem('token')
        if (token) {
          await refreshUser()
        }
      } catch {
        localStorage.removeItem('token')
        localStorage.removeItem('user')
      } finally {
        setIsLoading(false)
      }
    }
    initAuth()
  }, [])

  const login = async (username: string, password: string) => {
    const response = await apiLogin(username, password)
    localStorage.setItem('token', response.access_token)
    await refreshUser()
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setUser(null)
    const portalUrl = localStorage.getItem('sso_portal_login_url')
    if (portalUrl) {
      window.location.href = portalUrl
    }
  }

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin ?? false,
    isTrainer: user?.user_type === 'trainer',
    login,
    logout,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
